import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormBuilder, Validators, FormArray, FormGroup, Form } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService, SelectItem } from 'primeng/api';
import { InlineResponse } from '../../../../../core/models/inline-response.model';
import { Prestation } from '../prestation.model';
import { GenericErrorResponse } from '../../../../../core/models/generic-response.model';
import { ReferentialsPrestationsService } from '../referentials-prestations.service';
import { Subject, takeUntil } from 'rxjs';
import { TypePrestation } from '../type-prestation.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastModule } from 'primeng/toast';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';

export enum LienDechet {
  OBLIGATOIRE = 'OBLIGATOIRE',
  OPTIONNEL = 'OPTIONNEL',
  INTERDIT = 'INTERDIT',
}

@Component({
  selector: 'app-add-prestation',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, DynamicDialogModule, InputTextModule, InputGroupModule, InputGroupAddonModule, ConfirmDialogModule, ReactiveFormsModule, CardModule, ToggleButtonModule, FormsModule, DropdownModule, ToastModule, TranslocoModule],
  providers: [provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './add-prestation.component.html',
  styleUrl: './add-prestation.component.css',
})
export class AddPrestationComponent {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  typePrestationResult!: TypePrestation[];
  transport: boolean = false;

  userHasReadWritePermission: boolean = false;

  lienDechets: SelectItem[] = [
    { value: 'OPTIONNEL', label: 'OPTIONNEL' },
    { value: 'OBLIGATOIRE', label: 'OBLIGATOIRE' },
    { value: 'INTERDIT', label: 'INTERDIT' },
  ];

  selectedLienDechet: LienDechet | undefined;
  prestationFormArray!: FormArray;
  fg!: FormGroup;
  errors: GenericErrorResponse[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private prestationService: ReferentialsPrestationsService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    if (!!this.config.data) {
      this.fg = this.config.data.prestationForm;
      this.prestationFormArray = this.fg.get('prestationFormArray') as FormArray;
      this.userHasReadWritePermission = this.config.data.userHasReadWritePermission;
    }
    this.loadTypePrestation();
  }

  prestationForm = this.formBuilder.group({
    codeHecate: ['', [Validators.required, Validators.maxLength(255)]],
    codeTennaxia: [null, [Validators.maxLength(50)]],
    designation: [null, [Validators.required, Validators.maxLength(100)]],
    lienDechet: [null, [Validators.required]],
    libelleCaracteristiques: [],
    codeDR: [],
    categorieAchatMosaic: ['', [Validators.required]],
    typeId: [null, [Validators.required, Validators.pattern('^[0-9]*$')]],
  });

  private loadTypePrestation() {
    this.prestationService
      .getTypePrestation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (typePresta) => {
          this.typePrestationResult = typePresta;
        },
        error: (error: HttpErrorResponse) => this.handleHttpErrorResponse(error),
      });
  }

  private checkIfPrestationSuccessfull(data: InlineResponse) {
    switch (data.success) {
      case true:
        let prestation = data.object as Prestation;
        this.prestationFormArray.push(
          this.formBuilder.group({
            id: [prestation.id],
            codeHecate: [prestation.codeHecate],
            designation: [prestation.designation],
            codeDR: [prestation.codeDR],
            libelleCaracteristiques: [prestation.libelleCaracteristiques],
            typeDesignation: [prestation.typeDesignation],
            typeCodeHecate: [prestation.typeCodeHecate],
            typeId: [prestation.typeId],
            variante: [prestation.variante],
            lienDechet: [prestation.lienDechet],
            categorieAchatMosaic: [prestation.categorieAchatMosaic],
            codeTennaxia: [prestation.codeTennaxia],
          }),
        );
        this.close(true);
        break;
      case false:
        this.errors = data.errors.length > 0 ? data.errors : [data.exception];
        this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue ' + this.errors.map((e) => e.errorMsg), life: 3000 });
        break;
    }
  }

  addPrestation() {
    this.prestationService
      .addPrestation(
        this.prestationForm.get('categorieAchatMosaic')?.value ?? '',
        this.prestationForm.get('codeDR')?.value ?? '',
        this.prestationForm.get('codeHecate')?.value ?? '',
        this.prestationForm.get('codeTennaxia')?.value ?? '',
        this.prestationForm.get('designation')?.value ?? '',
        this.prestationForm.get('libelleCaracteristiques')?.value ?? '',
        this.prestationForm.get('lienDechet')?.value ?? '',
        this.prestationForm.get('typeId')?.value ?? 0,
      )
      .subscribe({
        next: (data) => {
          this.checkIfPrestationSuccessfull(data);
        },
        error: (error) => {
          this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue ' + this.errors.map((e) => e.errorMsg), life: 3000 });
        },
      });
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  close(result: boolean): void {
    this.ref.close(result);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
